import { statemachine } from 'overmind';

import type { actions, CalculationTactic } from '..';
import { isCalculationSetResultAvailable } from '../actions';

type States =
  | {
      current: 'AWAITING';
      calculationContext: actions.CalculationContext;
    }
  | {
      current: 'AWAITING_TENANTS';
      calculationContext: actions.CalculationContext;
    }
  | {
      current: 'AWAITING_AUTHENTICATED';
      calculationContext: actions.CalculationContext;
    }
  | {
      current: 'AWAITING_SELECTED_TENANT';
      calculationContext: actions.CalculationContext;
    }
  | {
      current: 'AWAITING_APPLICATION_ACCESS_CONFIRMED';
      calculationContext: actions.CalculationContext;
    }
  | {
      current: 'IDLE';
    }
  | {
      current: 'READY';
      calculationContext: actions.CalculationContext;
    }
  | {
      current: 'CANNOT_START';
      calculationContext: actions.CalculationContext;
      reason: string;
      details: string;
    };

type Events =
  | {
      type: 'INITIALIZE';
      data: {
        calculationContext: actions.CalculationContext, calculationSetExecutionResults?: CalculationTactic
      };
    }
  | {
      type: 'INVALID_CALCULATION_CONTEXT';
      data: { invalidCalculationContext?: actions.CalculationContext };
    }
  | { type: 'NO_ACCESS_TO_PROVIDED_TENANT'; data: { details: string } }
  | { type: 'AUTHENTICATED' }
  | { type: 'TENANTS_AVAILABLE' }
  | { type: 'TENANT_SELECTED' }
  | { type: 'APPLICATION_HAS_ACCESS' }
  | { type: 'APPLICATION_HAS_NO_ACCESS' }
  | { type: 'RESET' };
const transition = <K extends States['current']>({
  to,
  withState
}: {
  to: K;
  withState?: any;
}) => ({
  current: to,
  ...Object.assign({}, JSON.parse(JSON.stringify(withState || {})))
});

export const externallyTriggeredCalculationSetExecution = statemachine<
  States,
  Events
>({
  IDLE: {
    INITIALIZE: ({calculationContext, calculationSetExecutionResults}) => {
      if(isCalculationSetResultAvailable(calculationSetExecutionResults)) {
        return transition({
          to: "READY",
          withState: { calculationContext: calculationContext }
        })
      }
      const gridVersionName = sanitizeGridVersionName(calculationContext);

      return transition({
        to: 'AWAITING',
        withState: {
          calculationContext: { ...calculationContext, gridVersionName }
        }
      });
    },
    INVALID_CALCULATION_CONTEXT: ({ invalidCalculationContext }) =>
      transition({
        to: 'CANNOT_START',
        withState: {
          calculationContext: invalidCalculationContext,
          reason: 'INCOMPLETE_CALCULATION_CONTEXT',
          details: toDetails(invalidCalculationContext)
        }
      })
  },
  CANNOT_START: {},
  AWAITING: {
    AUTHENTICATED: (_, state) =>
      transition({
        to: 'AWAITING_TENANTS',
        withState: { calculationContext: state.calculationContext }
      }),
    TENANTS_AVAILABLE: (_, state) =>
      transition({
        to: 'AWAITING_AUTHENTICATED',
        withState: { calculationContext: state.calculationContext }
      })
  },
  AWAITING_AUTHENTICATED: {
    AUTHENTICATED: (_, state) =>
      transition({
        to: 'READY',
        withState: { calculationContext: state.calculationContext }
      })
  },
  AWAITING_TENANTS: {
    TENANTS_AVAILABLE: (_, state) =>
      transition({
        to: 'AWAITING_SELECTED_TENANT',
        withState: { calculationContext: state.calculationContext }
      })
  },
  AWAITING_SELECTED_TENANT: {
    TENANT_SELECTED: (_, state) =>
      transition({
        to: 'AWAITING_APPLICATION_ACCESS_CONFIRMED',
        withState: { calculationContext: state.calculationContext }
      }),
    NO_ACCESS_TO_PROVIDED_TENANT: ({ details }) =>
      transition({
        to: 'CANNOT_START',
        withState: { reason: 'NO_ACCESS_TO_PROVIDED_TENANT', details }
      })
  },
  AWAITING_APPLICATION_ACCESS_CONFIRMED: {
    APPLICATION_HAS_ACCESS: (_, state) =>
      transition({
        to: 'READY',
        withState: { calculationContext: state.calculationContext }
      }),
    APPLICATION_HAS_NO_ACCESS: () =>
      transition({
        to: 'CANNOT_START',
        withState: { reason: 'NO_ACCESS_TO_APPLICATION' }
      })
  },
  READY: {
    RESET: () => transition({ to: 'IDLE' })
  }
});
type RequiredProperties = keyof actions.CalculationContext;

export const DEFAULT_GRID_VERSION_NAME = undefined;
function isVersionNameInvalid(gridVersionName: string | undefined): boolean {
  return gridVersionName === '' || gridVersionName === null;
}
function sanitizeGridVersionName(
  calculationContext: actions.CalculationContext
) {
  return isVersionNameInvalid(calculationContext.gridVersionName)
    ? DEFAULT_GRID_VERSION_NAME
    : calculationContext.gridVersionName;
}

function toDetails(
  invalidCalculationContext: actions.CalculationContext | undefined
) {
  if (invalidCalculationContext === undefined)
    return 'No calculation context was provided';

  const missingProperties = findRequiredMissingProperties(
    invalidCalculationContext
  ).join(', ');

  return `Cannot start execution. The following properties are missing: ${missingProperties}`;
}

function findRequiredMissingProperties(
  invalidCalculationContext: actions.CalculationContext
) {
  return (
    [
      'calculationSetId',
      'relatedObjectId',
      'tenantId'
    ] as unknown as RequiredProperties[]
  )
    .map(requiredProperty => [
      invalidCalculationContext[requiredProperty] === undefined,
      requiredProperty
    ])
    .filter(([isMissing, _]) => isMissing)
    .map(([_, missingRequiredProperty]) => missingRequiredProperty);
}
